import React, { useCallback, useState } from 'react';
import IntlTelInput from 'react-intl-tel-input';
import styled, { css } from 'styled-components';
import 'react-intl-tel-input/dist/main.css';
import { parsePhoneNumber } from 'libphonenumber-js';

import colors, { withOpacity } from '@front/common/src/utils/colors';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { SUPPORTED_PHONE_NUMBER_COUNTRIES } from '@front/common/src/constants/country';
import { CommonInputPhoneProps } from '@front/common/src/types/components';
import { INPUT_PADDING_HORIZONTAL, getInputColors } from '@front/common/src/components/input/styles';
import { EXCLUDED_COUNTRIES } from '@front/common/src/constants/phone';

import { boxShadow } from '../../styles/mixins';
import { TEXT_FONT } from '../../constants/styles';
import Icon from '../icon/icon';
import BottomMessage from '../input/components/bottom-message';
import AnimatedPlaceholder from '../input/components/animated-placeholder';
import { inputStyleReset } from '../utils';
import { Container, InputContainer, InputFrame, baseInputStyle, InputStyleProps } from '../input/components';

const FLAG_CONTAINER_WIDTH = 70;
const INPUT_LEFT = FLAG_CONTAINER_WIDTH + 5;
const ICON_SIZE = 12;

const countryListStyle = css`
  position: absolute;
  width: calc(100% + 20px);
  left: 0;
  font-family: ${TEXT_FONT};
  ${boxShadow.normal};
  border-radius: 8px;
  border: none;

  .country {
    padding: 10px;

    &.highlight {
      background-color: ${colors.ink10};
    }
  }
`;

// Inserted by react-intl-tel-input at the root of the components tree (on mobile devices only)
export const countryListMobileStyle = css`
  .iti-mobile .intl-tel-input.iti-container {
    position: fixed;
    inset: 0;
    padding: 30px;
    background-color: ${withOpacity(colors.ink100, 0.3)};

    .country-list {
      ${countryListStyle}
    }
  }
`;

const StyledInputContainer = styled(InputContainer)<InputStyleProps>`
  position: relative;
  width: 100%;
  padding-left: 0;

  &&& .intl-tel-input {
    width: 100%;

    .input-phone {
      ${inputStyleReset}
      ${baseInputStyle}
      padding-left: ${INPUT_LEFT}px;
      width: 100%;
    }

    &.expanded {
      + .input-icon {
        transform: rotate(180deg);
      }
    }

    .flag-container {
      .country-list {
        ${countryListStyle}
        max-height: 300px;
        margin-top: 10px;
      }

      .selected-flag {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        width: ${FLAG_CONTAINER_WIDTH}px;
        padding-left: ${INPUT_PADDING_HORIZONTAL}px;

        .arrow {
          display: none;
        }
      }
    }
  }
`;

const ArrowIcon = styled(Icon)`
  position: absolute;
  top: ${INPUT_PADDING_HORIZONTAL}px;
  left: 46px;
`;

const StyledAnimatedPlaceholder = styled(AnimatedPlaceholder)`
  padding-left: ${INPUT_LEFT}px;
`;

export type InputPhoneProps = CommonInputPhoneProps & {
  id: string;
  label: string;
  name: string;
  autoComplete?: string;
  message?: string;
  disabled?: boolean;
  preferredCountries?: string[];
  onlyCountries?: string[];
  defaultCountry?: string;
};

const InputPhone = ({
  id,
  value,
  label,
  placeholder,
  disabled = false,
  onBlur,
  onFocus,
  onChange,
  error,
  name,
  message,
  autoComplete,
  preferredCountries = SUPPORTED_PHONE_NUMBER_COUNTRIES,
  onlyCountries,
  defaultCountry = getCurrentCountry().toLowerCase(),
}: InputPhoneProps) => {
  const hasLabel = !!label;
  const hasValue = !!value;
  const [isFocused, setIsFocused] = useState(false);

  const inputColors = getInputColors({ error, hasValue, isFocused });

  const onInputFocus = useCallback(() => {
    setIsFocused(true);
    if (onFocus) onFocus();
  }, [onFocus]);

  const onInputBlur = useCallback(() => {
    setIsFocused(false);
    if (onBlur) onBlur();
  }, [onBlur]);

  const onPhoneNumberChange = (isValid: boolean, phoneValue: string, _: any, formattedValue: string) => {
    if (isValid) {
      try {
        const phoneNumber = parsePhoneNumber(formattedValue);
        onChange({
          isValid: !EXCLUDED_COUNTRIES.some(
            (country) => country.toLowerCase() === phoneNumber.country?.toLowerCase(),
          ),
          value: phoneValue,
          formattedValue,
        });
      } catch {
        onChange({ isValid: false, value: phoneValue, formattedValue });
      }
    } else {
      onChange({ isValid: false, value: phoneValue, formattedValue });
    }
  };

  return (
    <Container $isDisabled={disabled}>
      <InputFrame
        $hasHoverStyle={!disabled && !isFocused && !error}
        $inputColors={inputColors}
        $inputId='none' // Otherwise, InputFrame being a label prevents country list to show.
      >
        <StyledInputContainer $hasLabel={hasLabel} $isFocused={isFocused}>
          <IntlTelInput
            inputClassName='input-phone'
            defaultCountry={defaultCountry}
            onlyCountries={onlyCountries}
            preferredCountries={preferredCountries}
            excludeCountries={EXCLUDED_COUNTRIES}
            onPhoneNumberFocus={onInputFocus}
            onPhoneNumberChange={onPhoneNumberChange}
            onPhoneNumberBlur={onInputBlur}
            placeholder={isFocused ? placeholder : ''}
            value={value}
            disabled={disabled}
            fieldId={id}
            fieldName={name}
            autoComplete={autoComplete}
          />
          <ArrowIcon className='input-icon' name='chevron-down' size={ICON_SIZE} color={colors.ink100} />
          {hasLabel && (
            <StyledAnimatedPlaceholder
              label={label}
              inputId={id}
              isActive={isFocused || hasValue}
              color={inputColors.placeholder}
            />
          )}
        </StyledInputContainer>
      </InputFrame>
      <BottomMessage error={error} message={message} />
    </Container>
  );
};

export default InputPhone;
